import { PreconditionFailedError } from "@repo/domain";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@repo/ui/components/ui/alert-dialog";
import { cn } from "@repo/ui/lib/utils";
import { type ReactNode, useState } from "react";
import { toast } from "sonner";
import { isWithdrawnOrder, type PresentedOrderSetDto } from "@/lib/order-set-presentation";
import { qualifiedProcedureTitle } from "@/shared/lib/procedure-title";
import {
  EditableTimelineProvider,
  type EditableTimelineProviderProps,
  type EditableTimelineStep,
  editTimelineSteps,
  type TimelineDensity,
  TimelineEditor,
  useEditableTimeline,
} from "@/shared/timeline";

type OrderSetTimelineProviderProps = {
  orderSet: PresentedOrderSetDto;
  editable: boolean;
  readOnly?: boolean;
  density?: TimelineDensity;
  selectedStepId: string | null;
  onSelectionChange: (id: string | null) => void;
  onStepsChange: EditableTimelineProviderProps["onStepsChange"];
  onEnvelopeChange: EditableTimelineProviderProps["onEnvelopeChange"];
  /** Omitted where the surface cannot add work — a booked set, or a locked one. The control then renders nothing. */
  onAddProcedure?: EditableTimelineProviderProps["onAddProcedure"];
  onRemoveProcedure?: EditableTimelineProviderProps["onRemoveProcedure"];
  onMoveProcedure?: EditableTimelineProviderProps["onMoveProcedure"];
  /** Steps an unsubmitted booking proposal adds. */
  proposedStepIds?: ReadonlySet<string>;
  children: ReactNode;
};

type OrderSetTimelineEditorProps = {
  fillHeight?: boolean;
  /** Omitted where the surface has no reset of its own — a booked set discards from the bar instead. */
  onReset?: () => void;
  /** The surface’s commit bar, pinned under the grid. */
  commit?: ReactNode;
  /**
   * Work with no place on the track, shown under it — inside the scrolling body,
   * so the commit bar stays below it and pinned. Only where there is a bar; a
   * surface without one lays that work out after the frame itself.
   */
  untimed?: ReactNode;
  renderStepProcedures?: (stepId: string) => ReactNode;
};

/** Completed gestures enter the owning surface’s draft. */
export function OrderSetTimeline({
  fillHeight,
  onReset,
  commit,
  untimed,
  renderStepProcedures,
  ...provider
}: Omit<OrderSetTimelineProviderProps, "children" | "density"> & OrderSetTimelineEditorProps) {
  return (
    <OrderSetTimelineProvider {...provider} density={renderStepProcedures ? "editor" : "comfortable"}>
      <OrderSetTimelineEditor
        fillHeight={fillHeight}
        onReset={onReset}
        commit={commit}
        untimed={untimed}
        renderStepProcedures={renderStepProcedures}
      />
    </OrderSetTimelineProvider>
  );
}

/**
 * The order set's steps as editable timeline state. Everything under it — the
 * envelope grid, or any other surface drawing the same steps — shares one
 * selection, one drag runtime and one set of edit actions.
 */
export function OrderSetTimelineProvider({
  orderSet,
  editable,
  readOnly = false,
  density = "comfortable",
  selectedStepId,
  onSelectionChange,
  onStepsChange,
  onEnvelopeChange,
  onAddProcedure,
  onRemoveProcedure,
  onMoveProcedure,
  proposedStepIds,
  children,
}: OrderSetTimelineProviderProps) {
  const allSteps: EditableTimelineStep[] = orderSet.steps.map((step, index) => ({
    id: step.stepId,
    title: `Step ${index + 1}`,
    placement: step.placement,
    proposed: proposedStepIds?.has(step.stepId) ?? false,
    procedures: step.orders
      .filter((order) => !isWithdrawnOrder(order))
      .map((order) => ({
        id: order.orderId,
        title: qualifiedProcedureTitle(order.definition.title, order.qualifier),
        completed: order.status === "completed",
      })),
  }));
  // Withdrawn requests stay in the stored shape as history, but their vacated
  // steps are no longer timeline work. Preserve explicitly empty authored steps.
  const steps = allSteps
    .filter((step, index) => step.procedures.length > 0 || orderSet.steps[index]?.orders.length === 0)
    .map((step, index) => ({ ...step, title: `Step ${index + 1}` }));
  return (
    <EditableTimelineProvider
      steps={steps}
      density={density}
      envelopeMinutes={orderSet.envelopeMinutes}
      editable={editable}
      readOnly={readOnly}
      selectedId={selectedStepId}
      onSelectionChange={onSelectionChange}
      onStepsChange={({ steps: next, gesture }) => {
        const fullGesture =
          gesture.kind === "reorder"
            ? { ...gesture, toIndex: allSteps.findIndex((step) => step.id === steps[gesture.toIndex]?.id) }
            : gesture;
        const changed = next.find((step) => step.id === gesture.stepId);
        if (!changed) {
          onStepsChange({ steps: next, gesture: fullGesture });
          return;
        }
        // The visible result cannot be merged with history: unequal durations
        // can overlap hidden steps. Replay the gesture against the full shape,
        // just as the unbooked domain commands do, before emitting a proposal.
        try {
          onStepsChange({
            gesture: fullGesture,
            steps: editTimelineSteps(
              allSteps,
              fullGesture,
              gesture.kind === "resize" ? changed.placement.durationMinutes : changed.placement.offsetMinutes,
            ),
          });
        } catch (cause) {
          if (!(cause instanceof PreconditionFailedError)) throw cause;
          toast.error(cause.message);
        }
      }}
      onEnvelopeChange={onEnvelopeChange}
      onAddProcedure={onAddProcedure}
      onRemoveProcedure={onRemoveProcedure}
      onMoveProcedure={onMoveProcedure}
    >
      {children}
    </EditableTimelineProvider>
  );
}

/** The envelope grid over an `OrderSetTimelineProvider`, with its reset confirmation. */
export function OrderSetTimelineEditor({
  fillHeight = false,
  onReset,
  commit,
  untimed,
  renderStepProcedures,
}: OrderSetTimelineEditorProps) {
  const { meta } = useEditableTimeline();
  const [confirmReset, setConfirmReset] = useState(false);
  return (
    <div className={cn("space-y-3", fillHeight && "flex h-full min-h-0 flex-col")}>
      <TimelineEditor
        renderStepProcedures={renderStepProcedures}
        onReset={onReset ? () => setConfirmReset(true) : undefined}
        commit={commit}
        untimed={untimed}
        fillHeight={fillHeight}
      />

      <AlertDialog open={confirmReset} onOpenChange={setConfirmReset}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset step placement?</AlertDialogTitle>
            <AlertDialogDescription>
              Restore protocol step placement and visit duration. This also resets any manually chosen visit duration.
              Added procedure groups stay together and follow the protocol steps; the visit expands if needed to contain
              the retained work.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!meta.editable}
              onClick={() => {
                onReset?.();
                setConfirmReset(false);
              }}
            >
              Reset placement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
